// The zinc-puzzle document (core/src/doc.rs) and the library and solving API types.
import type { ConstraintEntry } from './constraints';
import type { InputMode } from './settings';

export type Rc = [number, number];

export interface PuzzleDoc {
  format: 'zinc-puzzle';
  version: 1;
  meta: { title: string; author: string; rules: string; finishMessage?: string | null };
  grid: {
    rows: number;
    cols: number;
    cells?: Rc[] | null;
    regions?: Rc[][] | null;
    boxRows: number;
    boxCols: number;
    digits: string[];
    subGrids?:
      | { row: number; col: number; rows: number; cols: number; boxRows: number; boxCols: number }[]
      | null;
  };
  givens: { cell: Rc; value: string }[];
  constraints: ConstraintEntry[];
  cosmetics: Cosmetic[];
  solution?: string[] | null;
  fog?: FogSpec | null;
}

/**
 * Fog of war, as SudokuPad plays it: `visible` is lit from the start. Without triggers, each
 * correct digit the player places clears the 3×3 around it. With triggers (SudokuPad's dynamic
 * fog), only the triggers clear anything; there is no 3×3.
 */
export interface FogSpec {
  visible: Rc[];
  reveal: string;
  triggers?: FogTrigger[];
  /** Cells the rules ask the player to guess: they try digits there until the fog clears. */
  trial?: Rc[];
}

/**
 * Once every cell in `when` holds its correct digit, and the player placed at least one of
 * them (givens light nothing on their own), the cells in `light` clear.
 */
export interface FogTrigger {
  when: Rc[];
  light: Rc[];
}

/** Drawing without logic, in cell units ([row, col], 0.5 = centre of the first cell). */
export type Cosmetic =
  | { kind: 'line'; points: [number, number][]; color: string; width: number; fill?: string }
  | {
      kind: 'arrow';
      points: [number, number][];
      color: string;
      width: number;
      headLength?: number;
    }
  | {
      kind: 'shape';
      layer: 'under' | 'over';
      center: [number, number];
      width: number;
      height: number;
      rounded?: boolean;
      fill?: string;
      stroke?: string;
      strokeWidth?: number;
      text?: string | number;
      textColor?: string;
      fontSize?: number;
      angle?: number;
      opacity?: number;
    }
  | { kind: 'cage'; cells: Rc[]; label?: string; color?: string; style?: string };

export type PuzzleStatus = 'draft' | 'ready' | 'needs-plugin';
export type SolveProgress = 'not-started' | 'in-progress' | 'solved';

export interface PersonStatus {
  progress: SolveProgress;
  /**
   * The solve time, only when it may be shown: your own, or the other person's once you've
   * finished too. While in progress, the time so far (your own only).
   */
  timeMs: number | null;
}

export interface PuzzleSummary {
  id: string;
  title: string;
  setter: string;
  source: string;
  sourceUrl: string | null;
  origin: 'hand-set' | 'generated';
  status: PuzzleStatus;
  rows: number;
  cols: number;
  constraintTypes: string[];
  score: number | null;
  createdAt: string;
  mine: PersonStatus;
  other: PersonStatus | null;
}

/** One technique on a puzzle's logical solve path (core/src/logic/score.rs). */
export interface TechniqueUse {
  /** kebab-case technique id, e.g. "hidden-single", "rule", "contradiction". */
  technique: string;
  /** The rule type, for layer-2 ("rule") steps. */
  rule: string | null;
  layer: number;
  count: number;
  /** The hardest use of it, on the 0–10 scale. */
  cost: number;
}

/** How a score came about: the hardest step, how many hard steps, every technique used. */
export interface ScoreBreakdown {
  score: number;
  hardest: TechniqueUse;
  steps: number;
  hardSteps: number;
  /** Rounds in the longest contradiction chain (0 without chains). */
  longestChain: number;
  techniques: TechniqueUse[];
}

export type Thumb = 'up' | 'down';

export interface PuzzleDetail extends PuzzleSummary {
  document: PuzzleDoc;
  scoreBreakdown: ScoreBreakdown | null;
  /** Your own thumbs up or down, if you gave one. */
  myRating: Thumb | null;
}

export interface RatingRequest {
  rating: Thumb | null;
}

export interface RatingResponse {
  myRating: Thumb | null;
}

/** One input in the move log. `cells` are cell indices in core order. */
export interface Move {
  kind:
    | 'digit'
    | 'corner'
    | 'centre'
    | 'position'
    | 'pencil'
    | 'colour'
    | 'pen'
    | 'delete'
    | 'undo'
    | 'redo'
    | 'clear'
    | 'nudge'
    | 'fill';
  cells: number[];
  value?: string | number | null;
  /** Milliseconds of solving time when the move was made. */
  t: number;
  /** Digit moves: the digit also left its peers' marks (the "clear notes" setting). */
  auto?: boolean;
  /** Nudge moves (level 4, "apply it"): digits placed, wrong digits cleared, and cells whose
   * marks (`markKey`) become exactly the listed digits. One move, so one undo. */
  place?: [number, string][];
  clear?: number[];
  marks?: [number, string[]][];
  /** Nudge moves: the marks the cells get. Fill moves: the marks every digit goes into. Position
   * marks are SudokuPad mode's third kind: each digit in its own spot (1 top left, 9 bottom right).
   * ("Fill all candidates": the empty cells among `cells` get every digit). */
  markKey?: 'centre' | 'corner' | 'position';
}

/** A nudge from the solver core (core/src/logic/nudge.rs). Cells are [row, col]. */
export interface NudgeAnswer {
  kind: 'mistake' | 'step' | 'solved' | 'unavailable';
  /** Levels 1–3: where to look, what to think about, the deduction. */
  levels: { text: string; cells: Rc[] }[];
  /** Level 4 in words. */
  apply: string;
  technique: string | null;
  place: [Rc, string][];
  clear: Rc[];
  eliminate: [Rc, string][];
  remaining: [Rc, string[]][];
  message: string | null;
}

export interface SolveDto {
  id: string;
  puzzleId: string;
  mode: InputMode;
  /** The grid after the last move (web app format). */
  state: unknown;
  moves: Move[];
  elapsedMs: number;
  nudges: number;
  mistakes: number;
  finishedAt: string | null;
  finalTimeMs: number | null;
  /** The helper's marks (Σ), by clue. */
  helper: HelperMarks;
}

/** A helper mark: 1 green (in / this combination), 2 red (not in / not this one). */
export type HelperMark = 1 | 2;

/** The helper's marks for one clue: on its combinations (by key) and its digits. */
export interface ClueMarks {
  rows: Record<string, HelperMark>;
  digits: Record<string, HelperMark>;
}

/** The helper's marks in a solve, by clue key (its type and cells). */
export type HelperMarks = Record<string, ClueMarks>;

export interface SolveResponse {
  puzzle: PuzzleDetail;
  solve: SolveDto;
}

export interface MovesRequest {
  /** How many moves the client had before these: the server's count must match. */
  baseCount: number;
  moves: Move[];
  state: unknown;
  elapsedMs: number;
  mistakes: number;
  nudges: number;
  mode: InputMode;
}

export interface MovesResponse {
  moveCount: number;
}

export interface FinishRequest {
  /** One symbol or null per cell, in core order. */
  values: (string | null)[];
  elapsedMs: number;
}

export interface FinishResponse {
  correct: boolean;
  solve: SolveDto;
  /** The other person's time, now that you've finished (if they have). */
  other: PersonStatus | null;
}

// ── Admin ───────────────────────────────────────────────────────────────────

export type JobState = 'queued' | 'running' | 'done' | 'failed' | 'paused';

export interface JobDto {
  id: string;
  type: string;
  state: JobState;
  progress: number;
  error: string | null;
  result: unknown;
  createdAt: string;
  finishedAt: string | null;
  /** An import's input: the link it was given or found (a video), and that page's title. */
  input?: string | null;
  inputTitle?: string | null;
}

export interface RerateResult {
  checked: number;
  changed: number;
  unrated: number;
  solverVersion: string;
}

/** Admin → Import: a link or code, and rules to use when the puzzle carries none. */
export interface ImportRequest {
  text: string;
  rules?: string;
}

export interface ImportResult {
  puzzleId: string;
  title: string;
  status: PuzzleStatus;
  format: string;
  duplicate: boolean;
  unmapped: { kind: string; detail: string; count: number }[];
  solutionCount: number;
  /** Rules the keyword matcher read from the rules text. */
  keywords: string[];
  /** The rule reader will look at what stays unmapped. */
  readingQueued: boolean;
}

/** A YouTube playlist pasted into Admin → Import: one import of its own per video. */
export interface PlaylistImportResult {
  playlist: string;
  /** Videos queued for import (private and deleted ones are skipped). */
  queued: number;
}

export function isPlaylistImport(
  r: ImportResult | PlaylistImportResult | null | undefined,
): r is PlaylistImportResult {
  return !!r && 'playlist' in r;
}
